<template>
  <div class="patient-od-image-upload">
    <PhotoActionSheet
      v-model="sheetOpen"
      :title="sheetTitle"
      test-id="photo-to-od"
      :pick-source="step === 'source'"
      :show-take="isTouch"
      :show-cancel="step === 'source'"
      :locked="sending"
      @file="onFileChosen"
    >
      <!-- STEP 2: which Open Dental category? Tapping a row only SELECTS it; the
           Send button below is the deliberate confirmation (Nilay, 2026-09-16). -->
      <template v-if="step === 'category'">
        <div class="od-photo-preview-wrap">
          <img
            v-if="previewUrl"
            :src="previewUrl"
            class="od-photo-preview"
            alt="Photo to send"
            data-test="photo-to-od-preview"
          />
        </div>

        <div v-if="categoriesState === 'loading'" class="od-category-status" data-test="photo-to-od-categories-loading">
          <i class="fa fa-spinner fa-spin"></i> Loading Open Dental categories…
        </div>

        <template v-else-if="categoriesState === 'error'">
          <div class="od-category-status text-danger" data-test="photo-to-od-categories-error">
            Couldn't load Open Dental categories.
          </div>
          <button
            type="button"
            class="photo-sheet-row"
            data-test="photo-to-od-categories-retry"
            @click="loadCategories(true)"
          >
            <i class="fa fa-refresh"></i><span>Retry</span>
          </button>
        </template>

        <div
          v-else-if="categoriesState === 'loaded' && !orderedCategories.length"
          class="od-category-status"
          data-test="photo-to-od-categories-empty"
        >
          No image categories found in Open Dental.
        </div>

        <div v-else-if="categoriesState === 'loaded'" role="radiogroup" aria-label="Open Dental category">
          <button
            v-for="cat in orderedCategories"
            :key="cat.DefNum"
            type="button"
            role="radio"
            class="photo-sheet-row od-category-row"
            :class="{ 'is-selected': isSelected(cat) }"
            :aria-checked="isSelected(cat) ? 'true' : 'false'"
            :disabled="sending"
            :data-test="`photo-to-od-category-${cat.DefNum}`"
            @click="selectCategory(cat.DefNum)"
          >
            <i class="fa" :class="isSelected(cat) ? 'fa-check-circle' : 'fa-circle-thin'"></i>
            <span class="od-category-name">{{ cat.ItemName }}</span>
            <span v-if="isLastUsed(cat)" class="od-category-last-used" data-test="photo-to-od-last-used">Last used</span>
          </button>
        </div>
      </template>

      <!-- STEP 3: sent. "Take another" keeps the category selected (front, then back
           of a card) — Send is still required for the next photo. -->
      <template v-if="step === 'sent'">
        <div class="od-sent-status" data-test="photo-to-od-sent">
          <i class="fa fa-check-circle"></i>
          <span>Sent to Open Dental → {{ lastSentName }}</span>
        </div>
        <button
          type="button"
          class="photo-sheet-row"
          data-test="photo-to-od-take-another"
          @click="takeAnother"
        >
          <i class="fa fa-camera"></i><span>Take another ({{ lastSentName }})</span>
        </button>
        <button
          type="button"
          class="photo-sheet-row"
          data-test="photo-to-od-done"
          @click="sheetOpen = false"
        >
          <i class="fa fa-check"></i><span>Done</span>
        </button>
      </template>

      <template v-if="step === 'category'" #footer>
        <div class="od-send-footer">
          <button
            type="button"
            class="btn btn-light od-cancel-btn"
            data-test="photo-to-od-cancel"
            :disabled="sending"
            @click="sheetOpen = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn od-send-btn"
            data-test="photo-to-od-send"
            :disabled="!canSend"
            @click="send"
          >
            <i v-if="sending" class="fa fa-spinner fa-spin mr-1"></i>{{ sendLabel }}
          </button>
        </div>
      </template>
    </PhotoActionSheet>
  </div>
</template>

<script>
import PhotoActionSheet from './PhotoActionSheet.vue';
import { maskState } from '@/utils/phiMaskState';
import { extractApiError } from '@/utils/apiError';
import { hasTouchScreen } from '@/utils/deviceType';

/** Per-device convenience only: which category this browser sent to last, per site. */
const LAST_USED_KEY_PREFIX = 'teamio_photo_to_od_last_category:';

/** savePatientOdImage's 400 when the picked category was deleted/hidden since the list loaded. */
const CATEGORY_GONE = /no longer exists/i;

function readLastUsed(siteId) {
  try {
    return window.localStorage.getItem(LAST_USED_KEY_PREFIX + siteId) || null;
  } catch (e) {
    return null;
  }
}

function writeLastUsed(siteId, defNum) {
  try {
    window.localStorage.setItem(LAST_USED_KEY_PREFIX + siteId, String(defNum));
  } catch (e) {
    // Private mode / blocked storage: the list just won't pin a "Last used" row.
  }
}

/**
 * "Photo to OD" — staff walking the office with a phone photograph a document
 * (insurance card, medication list…) and file it into the Open Dental image
 * category they pick. Requested by Harrisburg Smiles, 2026-09-16.
 *
 * FLOW: source (Take / Upload) → category (preview + list + Send) → sent
 * (Take another / Done). Opened by PatientProfile's "Photo to OD" button through
 * open(); the button and its upgrade popover live in PatientProfile.vue so they
 * share that page's action-row styles.
 *
 * ⛔ NOT THE PROFILE PHOTO. Different endpoints, a fresh S3 key per upload, and
 * nothing kept in Teamio once OD has it (see store/patientPhoto.js uploadToOd).
 *
 * ⛔ THE SEND BUTTON IS THE CONFIRMATION. A thumb that starts a scroll on a list
 * row often lands as a tap, and a photo filed under the wrong category has no undo
 * in Teamio. So: a tap only selects, nothing is ever pre-selected (not even the
 * last-used category, which is only pinned to the top), and the button names the
 * category it is about to send to.
 *
 * ⛔ DEMO MASK. Same rule as the avatar: masked ⇒ nothing opens. Read from the
 * reactive phiMaskState, never allSettings.
 *
 * ⛔ PATIENT SWITCH. The profile is reused across patients (family links change the
 * route param without remounting), so the photo is bound to the patient it was
 * taken for, and a patient change discards it — never file patient A's card into
 * patient B's chart.
 */
export default {
  name: 'PatientOdImageUpload',

  components: { PhotoActionSheet },

  props: {
    siteId: { type: String, required: true },
    patNum: { type: [String, Number], required: true },
    enabled: { type: Boolean, default: false },
  },

  data() {
    return {
      sheetOpen: false,
      // 'source' | 'category' | 'sent'
      step: 'source',
      // Re-read in open(): touch emulation can attach after the page starts.
      isTouch: hasTouchScreen(),
      file: null,
      previewUrl: null,
      capturedPatNum: null,
      categories: [],
      // 'idle' | 'loading' | 'loaded' | 'error'
      categoriesState: 'idle',
      selectedDefNum: null,
      lastUsedDefNum: null,
      sending: false,
      lastSentName: '',
    };
  },

  computed: {
    masked() {
      const m = maskState();
      return !!(m && (m.userMask || m.siteMask));
    },

    interactive() {
      return this.enabled && !this.masked;
    },

    /** OD's own order, with the last-used category (if still listed) moved to the top. */
    orderedCategories() {
      const last = this.lastUsedDefNum;
      if (last === null) return this.categories;
      const i = this.categories.findIndex((c) => String(c.DefNum) === String(last));
      if (i < 1) return this.categories;
      return [this.categories[i], ...this.categories.slice(0, i), ...this.categories.slice(i + 1)];
    },

    selectedCategory() {
      if (this.selectedDefNum === null) return null;
      return this.categories.find((c) => String(c.DefNum) === String(this.selectedDefNum)) || null;
    },

    canSend() {
      return !!(this.file && this.selectedCategory && !this.sending && this.categoriesState === 'loaded');
    },

    sendLabel() {
      if (this.sending) return 'Sending…';
      return this.selectedCategory ? `Send to ${this.selectedCategory.ItemName}` : 'Select a category';
    },

    sheetTitle() {
      if (this.step === 'category') return 'File in Open Dental as…';
      if (this.step === 'sent') return 'Sent to Open Dental';
      return 'Photo to Open Dental';
    },
  },

  watch: {
    patNum() {
      this.close();
    },

    interactive(v) {
      if (!v) this.close();
    },

    // Closing past the source step (Cancel, backdrop, Done) throws the photo away.
    // Closing IN the source step is the sheet getting out of the camera's way.
    sheetOpen(v) {
      if (!v && this.step !== 'source' && !this.sending) this.reset();
    },
  },

  beforeDestroy() {
    this.revokePreview();
  },

  methods: {
    /** Called by PatientProfile's "Photo to OD" button. */
    open() {
      // Guard, not just the button's :disabled — this is the entitlement/mask boundary.
      if (!this.interactive) return;
      this.reset();
      this.isTouch = hasTouchScreen();
      this.lastUsedDefNum = readLastUsed(this.siteId);
      this.sheetOpen = true;
    },

    close() {
      this.sheetOpen = false;
      if (!this.sending) this.reset();
    },

    reset() {
      this.revokePreview();
      this.file = null;
      this.capturedPatNum = null;
      this.selectedDefNum = null;
      this.lastSentName = '';
      this.step = 'source';
    },

    revokePreview() {
      if (this.previewUrl) {
        try { URL.revokeObjectURL(this.previewUrl); } catch (e) { /* nothing to release */ }
      }
      this.previewUrl = null;
    },

    onFileChosen(file) {
      if (!file || !this.interactive) return;
      this.revokePreview();
      this.file = file;
      try {
        this.previewUrl = URL.createObjectURL(file);
      } catch (e) {
        this.previewUrl = null;
      }
      this.capturedPatNum = this.patNum;
      this.step = 'category';
      this.sheetOpen = true;
      this.loadCategories();
    },

    async loadCategories(force = false) {
      if (!force && (this.categoriesState === 'loaded' || this.categoriesState === 'loading')) return;
      this.categoriesState = 'loading';
      try {
        this.categories = await this.$store.dispatch('patientPhoto/loadOdCategories', { siteID: this.siteId });
        this.categoriesState = 'loaded';
        // A selection that is no longer listed can never be sent.
        if (this.selectedDefNum !== null && !this.selectedCategory) this.selectedDefNum = null;
      } catch (err) {
        console.warn('[PatientOdImageUpload] could not load OD image categories:', err);
        this.categoriesState = 'error';
      }
    },

    isSelected(cat) {
      return this.selectedDefNum !== null && String(cat.DefNum) === String(this.selectedDefNum);
    },

    isLastUsed(cat) {
      return this.lastUsedDefNum !== null && String(cat.DefNum) === String(this.lastUsedDefNum);
    },

    selectCategory(defNum) {
      if (this.sending) return;
      this.selectedDefNum = defNum;
    },

    async send() {
      if (!this.canSend) return;
      if (String(this.capturedPatNum) !== String(this.patNum)) {
        this.close();
        return;
      }

      const category = this.selectedCategory;
      this.sending = true;
      try {
        const data = await this.$store.dispatch('patientPhoto/uploadToOd', {
          siteID: this.siteId,
          patNum: this.capturedPatNum,
          file: this.file,
          categoryDefNum: category.DefNum,
        });
        const name = (data && data.categoryName) || category.ItemName;
        writeLastUsed(this.siteId, category.DefNum);
        this.lastUsedDefNum = String(category.DefNum);
        this.$toast.success(`Sent to Open Dental → ${name}`);
        this.$emit('sent', { category: category.DefNum, categoryName: name, fileName: data && data.fileName });
        this.revokePreview();
        this.file = null;
        this.lastSentName = name;
        this.step = 'sent';
      } catch (err) {
        // Keep the photo and the selection on screen: Send stays available to retry.
        const message = extractApiError(err, 'Could not send the photo to Open Dental. Please try again.');
        this.$toast.error(message);
        if (err && err.response && err.response.status === 400 && CATEGORY_GONE.test(message)) {
          this.selectedDefNum = null;
          this.loadCategories(true);
        }
      } finally {
        this.sending = false;
        // Closed mid-send (patient switch, mask turned on): drop what is left.
        if (!this.sheetOpen) this.reset();
      }
    },

    takeAnother() {
      // Selection kept on purpose; the next photo still needs its own Send.
      this.isTouch = hasTouchScreen();
      this.step = 'source';
    },
  },
};
</script>

<style lang="scss" scoped>
.od-photo-preview-wrap {
  display: flex;
  justify-content: center;
  padding: 0 16px 12px;
}

.od-photo-preview {
  display: block;
  max-width: 100%;
  max-height: 160px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  object-fit: contain;
  background: #f8fafc;
}

.od-category-status {
  padding: 14px 16px;
  font-size: 0.9375rem;
  line-height: 1.4;
  color: #64748b;
  border-top: 1px solid #eef2f7;
}

.od-category-row {
  &.is-selected {
    background: #ecfdf5;
    font-weight: 600;
  }

  .fa-circle-thin {
    color: #94a3b8;
  }
}

.od-category-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.od-category-last-used {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
}

.od-sent-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 16px 14px;
  font-size: 0.9375rem;
  line-height: 1.4;
  color: #0f7259;
  font-weight: 600;

  i {
    font-size: 1.25rem;
  }
}

.od-send-footer {
  display: flex;
  gap: 10px;
}

.od-cancel-btn {
  flex: 0 0 auto;
  min-height: 48px;
  // The sheet sits on the app's grey modal body; a btn-light alone read as plain text.
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #334155;
}

.od-send-btn {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 48px;
  background: #0f7259;
  border-color: #0f7259;
  color: #fff;
  font-weight: 600;
  white-space: normal;
  line-height: 1.25;

  &:disabled {
    background: #94a3b8;
    border-color: #94a3b8;
    opacity: 1;
  }
}
</style>
